/**
 * Datos de vista previa para la vertical Tours (SOLO dev/preview).
 *
 * Mapea `tours-package/data/tours.catalog.json` a los tipos de dominio para
 * poder renderizar las pantallas reales sin backend. Se elimina junto con
 * `app/preview/` cuando termine la iteración visual.
 */
import catalogJson from '../tours-package/data/tours.catalog.json'
import toursPhotosJson from '../tours-package/data/tours.photos.json'
import { env } from '../lib/env'
import { isIsolatedPreviewBuild } from '../lib/runtime-build'
import { hostNetToGross, toursPerPersonFromVehicleCents } from '../lib/tours/pricing'
import { previewToursCoverUrl, previewToursGalleryUrl } from '../lib/tours/preview-fixture-assets'
import { formatPrice } from '../lib/utils/format'
import type {
  TourCatalogExperience,
  TourCategory,
  TourExperienceDetail,
  TourGalleryImage,
  TourStopKind,
  TourTravelInfo,
  TourVehicleOption,
} from '../lib/types/tours.types'

const PAYOUT_SHARE = (1 - 0.15) * (1 - 0.035 - 0)
const previewCoverUrl = (slug: string) =>
  previewToursCoverUrl(slug, {
    supabaseUrl: env.EXPO_PUBLIC_SUPABASE_URL,
    isIsolatedPreviewBuild,
  })
const VEHICLE_LABEL: Record<string, string> = {
  microbus: 'Microbús',
  coaster: 'Coaster',
  bus: 'Bus',
  van_ejecutiva: 'Van Ejecutiva',
}
const CATEGORY_EN: Record<string, string> = {
  'viajes-ejecutivos': 'Executive Travel',
  eventos: 'Events',
}

const UNIT_BY_VEHICLE: Record<
  string,
  { label: string; makeModel: string; seats: number; cargoVolumeM3: number }
> = Object.fromEntries(
  catalogJson.units.map((unit) => [
    unit.id.replace('tourunit_tago_', ''),
    {
      label: unit.label,
      makeModel: unit.makeModel,
      seats: unit.seats,
      cargoVolumeM3: unit.cargoVolumeM3,
    },
  ]),
)

type Route = (typeof catalogJson)['routes'][number]

type ToursPhotoManifest = {
  licensePolicy: { adaptation: string }
  routes: Record<
    string,
    {
      coverId: string
      photos: Array<{
        id: string
        label: string
        alt: string
        credit: {
          title: string
          author: string
          license: string
          licenseUrl: string
          sourceUrl: string
        }
      }>
    }
  >
}

const toursPhotos = toursPhotosJson as ToursPhotoManifest

function routeCountry(route: Route, key: 'originCountry' | 'destinationCountry'): string {
  return (route as unknown as Record<string, string | undefined>)[key] ?? 'SV'
}

function previewGalleryForRoute(route: Route): TourGalleryImage[] {
  const collection = toursPhotos.routes[route.slug]
  if (!collection) return []

  return collection.photos.flatMap((photo) => {
    const imageUrl = previewToursGalleryUrl(route.slug, photo.id, {
      supabaseUrl: env.EXPO_PUBLIC_SUPABASE_URL,
      isIsolatedPreviewBuild,
    })
    if (!imageUrl) return []
    return [
      {
        id: photo.id,
        label: photo.label,
        alt: photo.alt,
        imageUrl,
        isCover: photo.id === collection.coverId,
        attribution: {
          ...photo.credit,
          adaptation: toursPhotos.licensePolicy.adaptation,
        },
      },
    ]
  })
}

/** Mismo criterio que build-package.mjs: el host_net cuyo gross queda más cerca. */
function hostNetForTarget(target: number): number {
  const base = Math.floor(target * PAYOUT_SHARE)
  let best = { hostNet: Math.max(1, base), guest: 0, distance: Number.POSITIVE_INFINITY }
  for (let hostNet = Math.max(1, base - 8); hostNet <= base + 8; hostNet += 1) {
    const guest = hostNetToGross(hostNet)
    const distance = Math.abs(guest - target)
    if (distance < best.distance || (distance === best.distance && guest < best.guest)) {
      best = { hostNet, guest, distance }
    }
  }
  return best.hostNet
}

/** Opciones de vehículo del tour, con su precio total (charter). */
function vehicleOptions(route: Route): TourVehicleOption[] {
  return Object.entries(route.tariffs as unknown as Record<string, number>).map(
    ([vehicle, target]) => {
      const unit = UNIT_BY_VEHICLE[vehicle]
      if (!unit) throw new Error(`missing unit for ${vehicle} on ${route.slug}`)
      return {
        unitId: `tourunit_tago_${vehicle}`,
        label: unit.label,
        seats: unit.seats,
        cargoVolumeM3: unit.cargoVolumeM3,
        guestPriceCents: hostNetToGross(hostNetForTarget(target)),
      }
    },
  )
}

/** Cuota mínima por persona (gancho del catálogo). */
function fromPerPersonCents(vehicles: TourVehicleOption[]): number {
  if (vehicles.length === 0) return 0
  return Math.min(
    ...vehicles.map((v) => toursPerPersonFromVehicleCents(v.guestPriceCents, v.seats)),
  )
}

/** Espeja la línea de tarifas por vehículo que el seed agrega a la description. */
function finalDescription(route: Route): string {
  const entries = Object.entries(route.tariffs as unknown as Record<string, number>)
  const parts = entries.map(
    ([vehicle, target]) =>
      `${VEHICLE_LABEL[vehicle] ?? vehicle} ${formatPrice(hostNetToGross(hostNetForTarget(target)))}`,
  )
  const label = parts.length > 1 ? 'Tarifas por vehículo' : 'Tarifa por vehículo'
  const body = route.description.replace(/\s*Tarifas? por vehículo:.*$/s, '').trim()
  return `${body} ${label}: ${parts.join(' · ')}.`
}

/**
 * Instrucciones del tour (estructuradas) según si cruza o no frontera.
 * Espeja `travelInfoFor` del builder (tours-package/scripts/build-package.mjs)
 * para que el preview sea fiel al seed.
 */
function travelInfoFor(route: Route): TourTravelInfo | null {
  const origin = routeCountry(route, 'originCountry')
  const destination = routeCountry(route, 'destinationCountry')
  const international = destination !== origin
  const gallery = previewGalleryForRoute(route)
  if (international) {
    return {
      meetingPoint: {
        label: 'Plaza Salvador del Mundo, San Salvador',
        address: 'Frente al monumento, salida norte',
        note: 'Llegá 30 minutos antes para el chequeo de documentos.',
      },
      whatToBring: [
        'Pasaporte y una copia física',
        'Ropa para varios días según el itinerario',
        'Efectivo: dólares, lempiras (HN) y quetzales (GT)',
        'Medicinas personales y botiquín básico',
        'Cargador y powerbank',
      ],
      documents: {
        required: [
          'Pasaporte vigente (mínimo 6 meses de vigencia)',
          'Para salvadoreños: DUI vigente (Acuerdo CA-4)',
        ],
        recommended: ['Copia física de documentos', 'Seguro de viaje'],
        note: 'El Acuerdo CA-4 permite el libre tránsito entre El Salvador, Guatemala, Honduras y Nicaragua para sus nacionales. Ciudadanos de otros países deben verificar visa y vigencia antes de viajar.',
      },
      border: {
        crossings: [
          'Frontera El Amatillo (El Salvador → Honduras)',
          'Frontera El Florido (Honduras → Guatemala)',
        ],
        note: 'Los tiempos de cruce varían entre 30 minutos y 2 horas. Se pagan tasas de salida/entrada en cada frontera: llevá efectivo en la moneda local.',
        currencyNote: 'Dólares, lempiras (HN) y quetzales (GT) para tasas y compras.',
      },
      policies: [
        'Salida puntual: el vehículo no espera a quien llega tarde.',
        'Cancelación según la política de reembolso vigente.',
        'El equipaje se limita al volumen reservado (ver categorías).',
      ],
      legalNotice:
        'Tago transporta y coordina el viaje, pero NO se responsabiliza por rechazos en frontera por documentación vencida, incompleta o incorrecta. Es responsabilidad exclusiva de cada viajero contar con pasaporte y visas vigentes y cumplir los requisitos migratorios de cada país. Al reservar aceptás estos términos.',
      isInternational: true,
      gallery,
    }
  }
  return {
    meetingPoint: {
      label: 'Plaza Salvador del Mundo, San Salvador',
      address: 'Frente al monumento, salida norte',
      note: 'Llegá 15 minutos antes de la hora de salida.',
    },
    whatToBring: [
      'Agua para el día',
      'Protector solar y gorra',
      'Calzado cómodo',
      'Identificación (DUI o pasaporte)',
    ],
    documents: {
      required: ['DUI o documento de identidad vigente'],
      note: 'Sin identificación no se permite abordar.',
    },
    policies: [
      'Salida puntual: el vehículo no espera a quien llega tarde.',
      'Cancelación sin cargo hasta 24 h antes de la salida.',
      'El equipaje se limita al volumen reservado (ver categorías).',
    ],
    legalNotice:
      'Tago coordina el transporte y la experiencia. El viajero es responsable de llegar a tiempo al punto de encuentro y de su documentación personal. El itinerario puede ajustarse por clima o seguridad.',
    isInternational: false,
    gallery,
  }
}

function categoryFor(route: Route): TourCategory | null {
  const category = catalogJson.categories.find((c) => c.id === route.categoryId)
  if (!category) return null
  if (category.id === 'ruta-del-pacifico' || category.id === 'oriente-magico') return null
  return {
    id: `tourcat_${category.id.replace(/-/g, '_')}`,
    code: category.id,
    nameEs: category.name,
    nameEn: CATEGORY_EN[category.id] ?? category.name,
  }
}

export const previewCatalog: TourCatalogExperience[] = catalogJson.routes.map((route) => {
  const vehicles = vehicleOptions(route)
  return {
    id: route.experienceId,
    title: route.title,
    description: finalDescription(route),
    originLabel: route.originLabel,
    destinationLabel: route.destinationLabel,
    durationMinutes: route.durationMinutes,
    distanceKm: route.distanceKm,
    coverImageUrl: previewCoverUrl(route.slug),
    originCountryCode: routeCountry(route, 'originCountry'),
    destinationCountryCode: routeCountry(route, 'destinationCountry'),
    currency: 'USD',
    host: { displayName: 'Tago', businessName: 'Tago Rent' },
    vehicles,
    fromPerPersonCents: fromPerPersonCents(vehicles),
    category: categoryFor(route),
  }
})

export function previewExperience(id: string): TourExperienceDetail | null {
  const route = catalogJson.routes.find((r) => r.experienceId === id)
  if (!route) return null
  const vehicles = vehicleOptions(route)
  return {
    id: route.experienceId,
    title: route.title,
    description: finalDescription(route),
    originLabel: route.originLabel,
    destinationLabel: route.destinationLabel,
    durationMinutes: route.durationMinutes,
    distanceKm: route.distanceKm,
    coverImageUrl: previewCoverUrl(route.slug),
    originCountryCode: routeCountry(route, 'originCountry'),
    destinationCountryCode: routeCountry(route, 'destinationCountry'),
    currency: 'USD',
    host: { displayName: 'Tago', businessName: 'Tago Rent' },
    vehicles,
    travelInfo: travelInfoFor(route),
    category: categoryFor(route),
    stops: route.stops.map((stop, index) => ({
      id: `${route.experienceId}_stop_${index}`,
      position: index,
      label: stop.label,
      latitude: stop.lat,
      longitude: stop.lng,
      kind: stop.kind as TourStopKind,
    })),
  }
}
